import { memo, useCallback, useEffect, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { DialogCloseButton } from '../DialogCloseButton.js';
import styles from './AppModal.module.css';

export interface AppModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
  readonly title?: string;
  /** Spec sizes: small 420 / medium 640 / large 880 / xlarge 1200–1520. */
  readonly size?: 'small' | 'medium' | 'large' | 'xlarge';
  readonly closeOnOverlayClick?: boolean;
  /** Sticky footer slot (actions). Prefer over in-body action rows. */
  readonly footer?: ReactNode;
  /** When true, Escape and backdrop dismiss are blocked (busy-dismiss matrix). */
  readonly isBusy?: boolean;
  /**
   * Vertical placement. Default `top` pins the shell under the viewport top inset and
   * stretches to the bottom inset so height (and edges) stay stable across tab/content changes.
   * Use `center` only for short one-shot dialogs that should float mid-viewport.
   */
  readonly placement?: 'center' | 'top';
  /**
   * Body scrollport. Default `auto` keeps `.body { overflow-y: auto }` for ordinary modals.
   * Use `hidden` when a nested catalog/list must be the sole scrollport (create-dialog pattern).
   */
  readonly bodyOverflow?: 'auto' | 'hidden';
  /** Overrides default `app-modal` content test id (overlay/close keep shared ids). */
  readonly testId?: string;
  /**
   * Radix Dialog modal mode (default `true`). Prefer {@link permissionTrapReleased} for
   * geo OS sheets — flipping `modal` remounts Dialog.Content (Modal↔NonModal) and drops
   * in-flight locate state.
   */
  readonly modal?: boolean;
  /**
   * Geo OS-permission trap release without remounting Dialog content.
   * Sets `data-geo-permission-trap="released"` and unlocks `document.body` pointer-events
   * so Android Chrome’s location sheet stays clickable over a live AppModal.
   */
  readonly permissionTrapReleased?: boolean;
}

const SIZE_CLASS: Record<NonNullable<AppModalProps['size']>, string> = {
  small: styles.sizeSmall as string,
  medium: styles.sizeMedium as string,
  large: styles.sizeLarge as string,
  xlarge: styles.sizeXlarge as string,
};

export const AppModal = memo<AppModalProps>(({
  isOpen,
  onClose,
  children,
  title,
  size = 'medium',
  closeOnOverlayClick = true,
  footer,
  isBusy = false,
  placement = 'top',
  bodyOverflow = 'auto',
  testId = 'app-modal',
  modal = true,
  permissionTrapReleased = false,
}) => {
  const { t } = useTranslation('admin');

  const dismissAllowed = !isBusy;
  const trapReleased = permissionTrapReleased || modal === false;

  useEffect(() => {
    if (!trapReleased || !isOpen) {
      return;
    }
    const body = document.body;
    const previous = body.style.pointerEvents;
    // Radix RemoveScroll sets body pointer-events:none; unlock so OS permission UI receives taps.
    body.style.setProperty('pointer-events', 'auto', 'important');
    return () => {
      if (previous) {
        body.style.pointerEvents = previous;
      } else {
        body.style.removeProperty('pointer-events');
      }
    };
  }, [trapReleased, isOpen]);

  const handleOpenChange = useCallback((open: boolean): void => {
    if (!open && dismissAllowed) {
      onClose();
    }
  }, [dismissAllowed, onClose]);

  const handleInteractOutside = useCallback((event: Event): void => {
    if (!closeOnOverlayClick || !dismissAllowed) {
      event.preventDefault();
    }
  }, [closeOnOverlayClick, dismissAllowed]);

  const handleEscapeKeyDown = useCallback((event: KeyboardEvent): void => {
    if (!dismissAllowed) {
      event.preventDefault();
    }
  }, [dismissAllowed]);

  const contentClassName = [
    styles.content,
    SIZE_CLASS[size],
    placement === 'top' ? styles.contentTop : styles.contentCenter,
  ].filter(Boolean).join(' ');

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={styles.overlay}
          data-testid="app-modal-overlay"
        />
        <Dialog.Content
          className={contentClassName}
          data-testid={testId}
          data-modal-size={size}
          data-modal-placement={placement}
          data-modal-busy={isBusy ? 'true' : undefined}
          data-geo-permission-trap={trapReleased ? 'released' : 'active'}
          aria-busy={isBusy || undefined}
          aria-describedby={undefined}
          onPointerDownOutside={handleInteractOutside}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          {title ? (
            <header className={styles.header}>
              <Dialog.Title asChild>
                <h2 className={styles.title}>{title}</h2>
              </Dialog.Title>
              <Dialog.Close asChild>
                <DialogCloseButton testId="app-modal-close" disabled={isBusy} />
              </Dialog.Close>
            </header>
          ) : (
            <Dialog.Title className={styles.visuallyHiddenTitle}>{t('shared.close')}</Dialog.Title>
          )}
          <div
            className={[
              'admin-scrollbar',
              styles.body,
              bodyOverflow === 'hidden' ? styles.bodyOverflowHidden : null,
            ].filter(Boolean).join(' ')}
            data-body-overflow={bodyOverflow}
          >
            {children}
          </div>
          {footer ? (
            <footer className={styles.footer} data-testid="app-modal-footer">
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

AppModal.displayName = 'AppModal';
